import { apiClient } from '@/lib/api/client'
import type {
  Backup,
  BackupSchedule,
  BackupStats,
  CreateBackupData,
  CreateScheduleData,
  DatabaseOption,
  RestoreOptions,
  ValidationResult,
  StorageDisk,
} from '@/types/backup'

export interface BackupListParams {
  page?: number
  per_page?: number
  type?: 'database' | 'files' | 'full'
  status?: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled'
  search?: string
}

export interface BackupListResponse {
  data: Backup[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export const BackupService = {
  /**
   * Get list of backups
   */
  async getBackups(params?: BackupListParams): Promise<BackupListResponse> {
    try {
      const response = await apiClient.get('/backups', { params })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch backups')
    }
  },

  /**
   * Get backup by ID
   */
  async getBackup(id: number): Promise<Backup> {
    if (!id || id <= 0) {
      throw new Error('Invalid backup ID')
    }
    try {
      const response = await apiClient.get(`/backups/${id}`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch backup')
    }
  },

  /**
   * Create backup
   */
  async createBackup(data: CreateBackupData): Promise<{ message: string; data: Backup }> {
    try {
      const response = await apiClient.post('/backups', data)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create backup')
    }
  },

  /**
   * Delete backup
   */
  async deleteBackup(id: number): Promise<void> {
    if (!id || id <= 0) {
      throw new Error('Invalid backup ID')
    }
    try {
      await apiClient.delete(`/backups/${id}`)
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete backup')
    }
  },

  /**
   * Download backup
   */
  async downloadBackup(id: number): Promise<Blob> {
    if (!id || id <= 0) {
      throw new Error('Invalid backup ID')
    }
    try {
      const response = await apiClient.get(`/backups/${id}/download`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to download backup')
    }
  },

  /**
   * Restore backup
   */
  async restoreBackup(id: number, options: RestoreOptions): Promise<{ message: string }> {
    if (!id || id <= 0) {
      throw new Error('Invalid backup ID')
    }
    try {
      const response = await apiClient.post(`/backups/${id}/restore`, options)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to restore backup')
    }
  },

  /**
   * Validate backup
   */
  async validateBackup(id: number): Promise<ValidationResult> {
    if (!id || id <= 0) {
      throw new Error('Invalid backup ID')
    }
    try {
      const response = await apiClient.get(`/backups/${id}/validate`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to validate backup')
    }
  },

  /**
   * Get backup statistics
   */
  async getBackupStats(): Promise<BackupStats> {
    try {
      const response = await apiClient.get('/backups/stats')
      // Handle both direct data and wrapped response
      const stats = response.data?.data || response.data
      
      // Ensure all required fields exist
      if (stats && typeof stats === 'object') {
        return {
          total: stats.total ?? 0,
          completed: stats.completed ?? 0,
          failed: stats.failed ?? 0,
          pending: stats.pending ?? 0,
          in_progress: stats.in_progress ?? 0,
          total_size: stats.total_size ?? 0,
          database_backups: stats.database_backups ?? 0,
          files_backups: stats.files_backups ?? 0,
          full_backups: stats.full_backups ?? 0,
        }
      }
      
      // Return default if stats is invalid
      return {
        total: 0,
        completed: 0,
        failed: 0,
        pending: 0,
        in_progress: 0,
        total_size: 0,
        database_backups: 0,
        files_backups: 0,
        full_backups: 0,
      }
    } catch (error: any) {
      // Return default stats instead of throwing
      console.error('Failed to fetch backup statistics:', error)
      return {
        total: 0,
        completed: 0,
        failed: 0,
        pending: 0,
        in_progress: 0,
        total_size: 0,
        database_backups: 0,
        files_backups: 0,
        full_backups: 0,
      }
    }
  },

  /**
   * Get schedules
   */
  async getSchedules(): Promise<BackupSchedule[]> {
    try {
      const response = await apiClient.get('/backups/schedules')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch schedules')
    }
  },

  /**
   * Get schedule by ID
   */
  async getSchedule(id: number): Promise<BackupSchedule> {
    if (!id || id <= 0) {
      throw new Error('Invalid schedule ID')
    }
    try {
      const response = await apiClient.get(`/backups/schedules/${id}`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch schedule')
    }
  },

  /**
   * Create schedule
   */
  async createSchedule(data: CreateScheduleData): Promise<{ message: string; data: BackupSchedule }> {
    try {
      const response = await apiClient.post('/backups/schedules', data)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create schedule')
    }
  },

  /**
   * Update schedule
   */
  async updateSchedule(id: number, data: Partial<CreateScheduleData>): Promise<{ message: string; data: BackupSchedule }> {
    if (!id || id <= 0) {
      throw new Error('Invalid schedule ID')
    }
    try {
      const response = await apiClient.put(`/backups/schedules/${id}`, data)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update schedule')
    }
  },

  /**
   * Delete schedule
   */
  async deleteSchedule(id: number): Promise<void> {
    if (!id || id <= 0) {
      throw new Error('Invalid schedule ID')
    }
    try {
      await apiClient.delete(`/backups/schedules/${id}`)
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete schedule')
    }
  },

  /**
   * Run schedule manually
   */
  async runSchedule(id: number): Promise<{ message: string }> {
    if (!id || id <= 0) {
      throw new Error('Invalid schedule ID')
    }
    try {
      const response = await apiClient.post(`/backups/schedules/${id}/run`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to run schedule')
    }
  },

  /**
   * Get list of databases
   */
  async getBackupDatabases(): Promise<DatabaseOption[]> {
    try {
      const response = await apiClient.get('/backups/options/databases')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch databases')
    }
  },

  /**
   * Get database tables list
   * @param database Optional database name to list tables from (uses default if omitted)
   */
  async getBackupTables(database?: string): Promise<string[]> {
    try {
      const response = await apiClient.get('/backups/options/tables', {
        params: database ? { database } : undefined,
      })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch tables')
    }
  },

  /**
   * Get storage disks
   */
  async getStorageDisks(): Promise<StorageDisk[]> {
    try {
      const response = await apiClient.get('/backups/options/disks')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch storage disks')
    }
  },
}

